import { useEffect, useState, useCallback } from 'react';
import { Plus, Search, X, Edit2, Trash2, ChevronDown, ChevronUp, Filter, Lock, ShieldCheck, KeyRound, Users as UsersIcon, MailCheck } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { UserFormDrawer } from '@/components/users/UserFormDrawer';
import { ConfirmModal } from '@/components/ConfirmModal';
import { UpgradePlanModal } from '@/components/UpgradePlanModal';
import { PasswordResetModal } from '@/components/PasswordResetModal';
import { TableRowSkeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { getCountries, countryIdByName, stateIdByName, cityIdByName } from '@/lib/geoApi';
import { extractErrorMessage } from '@/lib/errorMessage';
import { formatDateTime } from '@/lib/dateFormat';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import { SelectField } from '@/components/ui/SelectField';

interface User {
  id: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phone: string | null;
  role: string;
  department: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  isActive: boolean;
  isOwner: boolean;
  emailVerified: boolean;
  lastLogin: string | null;
  reportingManager: { id: string; name: string } | null;
}

interface Subscription {
  status: string;
  plan: { name: string; price: number } | null;
}

const ROLE_OPTIONS = ['admin', 'manager', 'counselor', 'agent'];
const roleColors: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-700',
  manager: 'bg-blue-100 text-blue-700',
  counselor: 'bg-yellow-100 text-yellow-700',
  agent: 'bg-green-100 text-green-700',
};
const selectClass = 'h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

export function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [managers, setManagers] = useState<{ id: string; name: string }[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [confirm, setConfirm] = useState<{ type: 'deactivate' | 'delete'; user: User } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState<string | null>(null);
  const [resetPwdUser, setResetPwdUser] = useState<User | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    search: '', role: '', department: '', country: '', state: '',
    city: '', reportingManagerId: '', isActive: '', page: 1,
  });

  const fetchUsers = useCallback(async (f = filters) => {
    setIsLoading(true);
    try {
      const params: any = { page: f.page, limit: 25 };
      if (f.search) params.search = f.search;
      if (f.role) params.role = f.role;
      if (f.department) params.department = f.department;
      // Prefer master ids; fall back to the typed name (backend matches via join).
      if (f.country) { const id = countryIdByName(f.country); if (id) params.countryId = id; else params.country = f.country; }
      if (f.state) { const id = stateIdByName(f.country, f.state); if (id) params.stateId = id; else params.state = f.state; }
      if (f.city) { const id = cityIdByName(f.country, f.state, f.city); if (id) params.cityId = id; else params.city = f.city; }
      if (f.reportingManagerId) params.reportingManagerId = f.reportingManagerId;
      if (f.isActive !== '') params.isActive = f.isActive;
      const { data } = await api.get('/users', { params });
      setUsers(data.users);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load users');
    }
    finally { setIsLoading(false); }
  }, [filters]);

  useEffect(() => { fetchUsers(filters); }, [filters]);

  // Preload the country master so country filters resolve to master ids.
  useEffect(() => { getCountries().catch(() => {}); }, []);

  useEffect(() => {
    // Managers / subscription are secondary signals — failure just hides
    // the manager dropdown and seat counter. console.warn so Sentry catches
    // it without spamming a toast on every page load.
    api.get('/users/managers')
      .then(r => setManagers(r.data))
      .catch(err => console.warn('Failed to load managers:', err?.message));
    api.get('/tenant/me')
      .then(r => setSubscription(r.data.subscription ?? null))
      .catch(err => console.warn('Failed to load subscription:', err?.message));
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(f => ({ ...f, search: searchInput, page: 1 }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({ search: '', role: '', department: '', country: '', state: '', city: '', reportingManagerId: '', isActive: '', page: 1 });
  };

  const handleToggleActive = (user: User) => {
    if (user.isOwner) { toast('error', 'Organization owner cannot be deleted or deactivated'); return; }
    setConfirm({ type: 'deactivate', user });
  };

  const handleDelete = (user: User) => {
    if (user.isOwner) { toast('error', 'Organization owner cannot be deleted or deactivated'); return; }
    setConfirm({ type: 'delete', user });
  };

  const handleVerifyEmail = async (user: User) => {
    try {
      await api.post(`/users/${user.id}/verify-email`);
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, emailVerified: true } : u));
      toast('success', `${user.name} marked as verified`);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to verify');
    }
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    try {
      if (confirm.type === 'deactivate') {
        await api.patch(`/users/${confirm.user.id}/toggle-active`);
        setUsers(prev => prev.map(u => u.id === confirm.user.id ? { ...u, isActive: !u.isActive } : u));
        toast('success', `${confirm.user.name} ${confirm.user.isActive ? 'deactivated' : 'activated'}`);
      } else {
        await api.delete(`/users/${confirm.user.id}`);
        fetchUsers(filters);
        toast('success', 'User deleted');
      }
      setConfirm(null);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Action failed');
    } finally {
      setConfirmLoading(false);
    }
  };

  const openEdit = (user: User) => { setEditUser(user); setShowDrawer(true); };

  const { sorted, sort, onSort } = useSort(users);

  const hasActiveFilters = filters.search || filters.role || filters.department || filters.country || filters.state || filters.city || filters.reportingManagerId || filters.isActive !== '';
  const hasActivePaidPlan = subscription?.status === 'active' && (subscription?.plan?.price ?? 0) > 0;

  return (
    <DashboardLayout title="User Management">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">Users</h1>
              {hasActivePaidPlan && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Active Plan · {subscription!.plan!.name}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{total} team members</p>
          </div>
          <Button onClick={() => { setEditUser(null); setShowDrawer(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Invite User
          </Button>
        </div>

        {/* Search + filter toggle */}
        <div className="flex flex-wrap gap-3 items-center">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9 w-64"
                placeholder="Search name or email..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
            </div>
            <Button type="submit" variant="outline">Search</Button>
          </form>

          <Button
            variant="outline"
            onClick={() => setShowFilters(v => !v)}
            className={showFilters ? 'border-blue-500 text-blue-600' : ''}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
          </Button>

          {hasActiveFilters && (
            <Button variant="ghost" size="default" onClick={clearFilters} className="text-muted-foreground">
              <X className="h-4 w-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-normal text-slate-600 mb-1 block">Role</label>
                  <SelectField className={`${selectClass} w-full`} value={filters.role} onChange={e => setFilters(f => ({ ...f, role: e.target.value, page: 1 }))}>
                    <option value="">All Roles</option>
                    {ROLE_OPTIONS.map(r => <option key={r} value={r} className="capitalize">{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                  </SelectField>
                </div>
                <div>
                  <label className="text-xs font-normal text-slate-600 mb-1 block">Department</label>
                  <Input placeholder="e.g. Sales" value={filters.department} onChange={e => setFilters(f => ({ ...f, department: e.target.value, page: 1 }))} />
                </div>
                <div>
                  <label className="text-xs font-normal text-slate-600 mb-1 block">Country</label>
                  <Input placeholder="e.g. India" value={filters.country} onChange={e => setFilters(f => ({ ...f, country: e.target.value, page: 1 }))} />
                </div>
                <div>
                  <label className="text-xs font-normal text-slate-600 mb-1 block">State</label>
                  <Input placeholder="e.g. Maharashtra" value={filters.state} onChange={e => setFilters(f => ({ ...f, state: e.target.value, page: 1 }))} />
                </div>
                <div>
                  <label className="text-xs font-normal text-slate-600 mb-1 block">City</label>
                  <Input placeholder="e.g. Mumbai" value={filters.city} onChange={e => setFilters(f => ({ ...f, city: e.target.value, page: 1 }))} />
                </div>
                <div>
                  <label className="text-xs font-normal text-slate-600 mb-1 block">Reporting Manager</label>
                  <SelectField className={`${selectClass} w-full`} value={filters.reportingManagerId} onChange={e => setFilters(f => ({ ...f, reportingManagerId: e.target.value, page: 1 }))}>
                    <option value="">All Managers</option>
                    {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </SelectField>
                </div>
                <div>
                  <label className="text-xs font-normal text-slate-600 mb-1 block">Status</label>
                  <SelectField className={`${selectClass} w-full`} value={filters.isActive} onChange={e => setFilters(f => ({ ...f, isActive: e.target.value, page: 1 }))}>
                    <option value="">All</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </SelectField>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <table className="w-full">
                <tbody>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <TableRowSkeleton key={i} columns={9} />
                  ))}
                </tbody>
              </table>
            ) : users.length === 0 ? (
              <EmptyState
                icon={UsersIcon}
                title="No team members yet"
                description="Invite the first user to your workspace so they can start working on leads."
                action={
                  <Button onClick={() => { setEditUser(null); setShowDrawer(true); }}>
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Invite user
                  </Button>
                }
              />
            ) : (
              <div className="overflow-x-auto overflow-y-visible">
                <table className="w-full text-sm">
                  <thead>
                    <TableHeadRow className="border-b-2 border-slate-200">
                      <SortTh label="Name" sortKey="name" sort={sort} onSort={onSort} />
                      <SortTh label="Email" sortKey="email" sort={sort} onSort={onSort} />
                      <SortTh label="Phone" sortKey="phone" sort={sort} onSort={onSort} />
                      <SortTh label="Country" sortKey="country" sort={sort} onSort={onSort} />
                      <SortTh label="Role" sortKey="role" sort={sort} onSort={onSort} />
                      <SortTh label="Reports To" sortKey="reportingManager.name" sort={sort} onSort={onSort} />
                      <SortTh label="Status" sortKey="isActive" sort={sort} onSort={onSort} />
                      <SortTh label="Last Login" sortKey="lastLogin" sort={sort} onSort={onSort} />
                      <SortTh label="Actions" sortable={false} />
                    </TableHeadRow>
                  </thead>
                  <tbody>
                    {sorted.map(user => {
                      const isOwnerWithPlan = user.isOwner && hasActivePaidPlan;
                      return (
                        <tr
                          key={user.id}
                          className={`border-b hover:bg-slate-50 transition-colors ${user.isOwner ? 'bg-indigo-50/30' : ''}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${user.isOwner ? 'bg-indigo-100' : 'bg-blue-100'}`}>
                                <span className={`text-xs font-semibold ${user.isOwner ? 'text-indigo-600' : 'text-blue-600'}`}>
                                  {(user.firstName || user.name || '?')[0].toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <p className="font-medium text-slate-900">{user.name}</p>
                                  {user.isOwner && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 border border-indigo-200 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                                      <ShieldCheck className="h-2.5 w-2.5" />
                                      Owner
                                    </span>
                                  )}
                                  {isOwnerWithPlan && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                      <Lock className="h-2.5 w-2.5" />
                                      Protected
                                    </span>
                                  )}
                                </div>
                                {user.department && <p className="text-xs text-muted-foreground">{user.department}</p>}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{user.email}</td>
                          <td className="px-4 py-3 text-slate-600">{user.phone || '—'}</td>
                          <td className="px-4 py-3 text-slate-600">{user.country || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${roleColors[user.role] || 'bg-slate-100 text-slate-600'}`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{user.reportingManager?.name || '—'}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleToggleActive(user)}
                              disabled={user.isOwner}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${user.isActive ? 'bg-blue-600' : 'bg-slate-300'}`}
                              title={user.isOwner ? 'Organization owner cannot be deleted or deactivated' : user.isActive ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                            >
                              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${user.isActive ? 'translate-x-4' : 'translate-x-0.5'}`} />
                            </button>
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs">
                            {user.lastLogin ? formatDateTime(user.lastLogin) : 'Never'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => openEdit(user)}
                                className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => setResetPwdUser(user)}
                                className="p-1.5 rounded hover:bg-amber-50 text-slate-500 hover:text-amber-600 transition-colors"
                                title="Reset Password"
                              >
                                <KeyRound className="h-3.5 w-3.5" />
                              </button>
                              {!user.emailVerified && (
                                <button
                                  onClick={() => handleVerifyEmail(user)}
                                  className="p-1.5 rounded hover:bg-emerald-50 text-slate-500 hover:text-emerald-600 transition-colors"
                                  title="Mark email as verified (bypass OTP)"
                                >
                                  <MailCheck className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {user.isOwner ? (
                                <span
                                  title="Organization owner cannot be deleted or deactivated"
                                  className="p-1.5 text-slate-300 cursor-default"
                                >
                                  <Lock className="h-3.5 w-3.5" />
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleDelete(user)}
                                  className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors"
                                  title="Deactivate"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-muted-foreground">
                  Page {filters.page} of {totalPages} · {total} users
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={filters.page <= 1}
                    onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>
                    ← Prev
                  </Button>
                  <Button variant="outline" size="sm" disabled={filters.page >= totalPages}
                    onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>
                    Next →
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {showDrawer && (
        <UserFormDrawer
          user={editUser}
          onClose={() => { setShowDrawer(false); setEditUser(null); }}
          onSaved={() => { setShowDrawer(false); setEditUser(null); fetchUsers(filters); }}
          onPlanLimit={(msg) => setUpgradeModal(msg)}
        />
      )}

      <UpgradePlanModal
        isOpen={!!upgradeModal}
        message={upgradeModal || ''}
        onClose={() => setUpgradeModal(null)}
      />

      <ConfirmModal
        isOpen={!!confirm}
        title={confirm?.type === 'deactivate'
          ? (confirm.user.isActive ? 'Deactivate User?' : 'Activate User?')
          : 'Delete User?'}
        message={confirm?.type === 'deactivate'
          ? (confirm.user.isActive
              ? `Are you sure you want to deactivate ${confirm.user.name}? They will not be able to login.`
              : `Are you sure you want to activate ${confirm.user.name}?`)
          : `Are you sure you want to delete ${confirm?.user.name}? This action cannot be undone.`}
        confirmText={confirm?.type === 'deactivate' ? (confirm.user.isActive ? 'Deactivate' : 'Activate') : 'Delete'}
        confirmButtonColor="red"
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
        isLoading={confirmLoading}
      />

      <PasswordResetModal
        isOpen={!!resetPwdUser}
        title="Reset User Password"
        subject={resetPwdUser ? `${resetPwdUser.name} · ${resetPwdUser.email}` : ''}
        onConfirm={async (newPassword) => {
          if (!resetPwdUser) return;
          await api.post(`/users/${resetPwdUser.id}/reset-password`, { newPassword });
          toast('success', `Password reset for ${resetPwdUser.email}`);
        }}
        onClose={() => setResetPwdUser(null)}
      />
    </DashboardLayout>
  );
}
